import React, { useEffect, useRef } from 'react'
import {observer} from 'mobx-react-lite'
import {useCustomerStore, useProductStore} from '../../store/hooks/useStore'
import {Box, Container, Stack, Text} from '../../vanilla'
import {HitItem} from '../algolia/instantsearch/hits'
import {useHitsProductBadges} from '../algolia/util/useHitsProductBadges'
import {Carousel} from '../carousel'
import {favHit, favItemSpacing, welcomeBackLinkBtn, welcomeBackTitleContainer, welcomeBackTitleLine, welcomeBackSubTitleLine} from './styles.css'
import {Hit, ProductModel} from '../../store/ProductStore'
import {CTAButton} from '../../vanilla/components/CTAButton'
import {CTA} from '../../types/cms'
import { useViewItemListTracking } from '../../analytics/view-item-list-tracking'
import { SlotComponent } from '../../types/ui'
import { useIntersectionObserver } from '../../hooks/use-intersection-observer'
interface WelcomeBackProps {
  cta?: CTA
  title?: string
  subtitle?: string
}
const WelcomeBackContainer:SlotComponent<WelcomeBackProps> = ({content}) => {
  const {isRegistered, wishlistHits} = useCustomerStore()
  const {productsById, getItemPrice} = useProductStore()
  const {title, cta, subtitle} = content
  useHitsProductBadges(wishlistHits as unknown as Hit[])
  const ref = useRef(null)
  const isOnScreen = useIntersectionObserver(ref, {
    useOnce: true,
    // Offset for intersection track. Sometimes a user can view the top of the product list section on a PDP,
    // but the actual products are still not visible, so adding an offset to ensure that the event only fires when the user has scrolled to and viewed the product items,
    // only then change observer state.
    rootMargin: '-150px 0px -150px 0px',
  })
  const sendViewItemListData = useViewItemListTracking()
  let filteredHits: Hit[] = []
  
  /* Send event to data layer when user scrolls to product carousel */
  useEffect(() => {
    if (filteredHits.length) {
      sendViewItemListData(filteredHits,
        {
          id: 'Wishlist-Carousel',
          name: 'Wishlist Carousel'
        })
    }
  }, [isOnScreen, filteredHits.length])
  if (!isRegistered || !wishlistHits.length) return null

  // Take the first 10 favourites and only show those that are available
  filteredHits = wishlistHits
    .filter(product => {
      const hit = productsById[product.id]
      if (!hit) return false

      const {displayPrice} = getItemPrice(hit as ProductModel)

      const isButtonDisabled = !displayPrice
        || hit.availableForDayAtStore === false
        || hit.isAddToCartDisabled === true

      return !isButtonDisabled
    })
    .slice(0, 10)
  
  return (
    <Box backgroundColor="gray50">
      <Container paddingY={['12px', '24px']}>
        <Stack spacing="40px">
          <Stack gap="2px" className={welcomeBackTitleContainer} position="relative" align="flex-start">
            {title && (
              <Text
                as="h2"
                variant="heading7"
                className={welcomeBackTitleLine}
              >
                {title}
              </Text>
            )}
            {subtitle && (
              <Text as="h2" variant="heading7" className={welcomeBackSubTitleLine}>
               {subtitle}
              </Text>
            )}
          </Stack>
          <Stack gap={['20px', '28px']} position="relative" ref={ref}>
            <Carousel
              loop={false}
              className="recommendations-carousel"
              itemClassName={favItemSpacing}
              controlsPosition="bottom"
              controlConfig={{
                activeDotColor: 'gray800',
                showArrows: {mobile: false, desktop: true, tablet: true},
                arrowsColor: 'gray800',
              }}
              customResponsiveConfig={{
                desktop: {
                  breakpoint: {min: 1024, max: 99999},
                  items: 5,
                  slidesToSlide: 5,
                },
                tablet: {
                  breakpoint: {max: 1024, min: 780},
                  items: 4,
                  slidesToSlide: 4,
                },
                mobile: {
                  breakpoint: {max: 780, min: 480},
                  items: 2,
                  slidesToSlide: 2,
                },
                'mobile-xs': {
                  breakpoint: {max: 480, min: 0},
                  items: 1,
                  slidesToSlide: 1,
                  partialVisibilityGutter: 50,
                },
              }}
            >
              {filteredHits.map((hit: Hit) => {
                return (
                  hit && (
                    <HitItem
                      hit={hit}
                      isPLPTile={true}
                      key={`wishlist-${hit.id}`}
                      className={favHit}
                    />
                  )
                )
              })}
            </Carousel>
            {cta?.ctaText && <CTAButton cta={cta} extraClassName={welcomeBackLinkBtn} alignSelf="center"></CTAButton>}
          </Stack>
        </Stack>
      </Container>
    </Box>
  )
}

export const WelcomeBack = observer(WelcomeBackContainer)